/**
 * ExtractedQuestionsView — parsed view for `extract_verbatim` /
 * `extract_verbatim_text` runs.
 *
 * Mirrors `VisualsView`: list what the model returned, and let an admin overlay
 * the boxes on the exact image they were computed against, because a diagram
 * crop that is subtly wrong is invisible in JSON but obvious on the page.
 *
 * Two differences from the visuals case:
 *
 *   1. MULTI-PAGE. A worksheet is several page images, and each box carries the
 *      `pageNumber` it belongs to. Boxes are grouped per page and overlaid on
 *      that page's image — a box drawn on the wrong page is a real failure mode
 *      worth being able to see.
 *   2. TWO COORDINATE FRAMES. Runs today return 0–1 fractions of the page,
 *      like extract_visuals. Older runs returned pixels against a 1000x1000
 *      image, and their model outputs are still viewable. Both are normalized
 *      here, at the view boundary, so `BboxOverlayModal` stays a
 *      pure-percentage overlay for both callers.
 *
 * The DOCX path produces no boxes and no page images; the question list still
 * renders and the overlay entry points simply don't appear.
 *
 * Text goes through the same `LatexText` the teacher and student apps use —
 * bare maths included — so this view shows what they would. It had its own
 * bare-LaTeX wrapper, and showed Q16's `\;` options cleanly while the teacher
 * app showed the source.
 */
import { useMemo, useState } from 'react';
import { LatexText } from '@shared-ui/latex';
import { BboxOverlayModal, type BboxSegment } from './BboxOverlayModal';

/** The 1000x1000 frame older extraction prompts told the model to use. */
const LEGACY_PIXEL_FRAME = 1000;

interface ExtractionBox {
    pageNumber?: number;
    label?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
}

interface ExtractedQuestion {
    questionNumber?: string | number;
    sourcePageNumber?: number;
    questionText?: string;
    questionType?: string;
    topic?: string;
    correctAnswer?: string;
    options?: { label?: string; text?: string }[];
    debug?: {
        questionDiagramBoundingBoxes?: ExtractionBox[];
        optionsDiagramBoundingBoxes?: ExtractionBox[];
    };
}

export interface ExtractedQuestionsViewProps {
    data: Record<string, unknown>;
    /** The page images actually sent to the model, in page order. */
    pageImages?: { url: string; label: string }[];
}

function isBox(b: unknown): b is ExtractionBox {
    if (!b || typeof b !== 'object') return false;
    const o = b as ExtractionBox;
    return typeof o.x === 'number' && typeof o.y === 'number'
        && typeof o.width === 'number' && typeof o.height === 'number';
}

/** A box → the modal's normalized [x, y, w, h]. A box already in fractions
 *  has every value at most 1; a pixel box in the 1000 frame never does. */
function toSegment(b: ExtractionBox, id: string): BboxSegment {
    const coords = [b.x!, b.y!, b.width!, b.height!];
    const scale = coords.every(n => n <= 1) ? 1 : LEGACY_PIXEL_FRAME;
    return {
        segment_id: id,
        bbox: coords.map(n => n / scale),
        semantic_description: b.label ? `option ${b.label} diagram` : 'question diagram',
    };
}

export function ExtractedQuestionsView({ data, pageImages }: ExtractedQuestionsViewProps) {
    const questions = Array.isArray(data.questions) ? (data.questions as ExtractedQuestion[]) : [];
    const [openPage, setOpenPage] = useState<number | null>(null);

    // Boxes grouped by the page they were reported on. A question's stem
    // diagram and its option diagrams are labelled by question number so the
    // overlay legend maps back to the list below.
    const boxesByPage = useMemo(() => {
        const byPage = new Map<number, BboxSegment[]>();
        questions.forEach((q, i) => {
            const qNo = String(q.questionNumber ?? i + 1);
            const push = (b: ExtractionBox, id: string) => {
                if (!isBox(b)) return;
                const page = b.pageNumber ?? q.sourcePageNumber ?? 1;
                const list = byPage.get(page) ?? [];
                list.push(toSegment(b, id));
                byPage.set(page, list);
            };
            (q.debug?.questionDiagramBoundingBoxes ?? []).forEach((b, j) => push(b, `Q${qNo}${j > 0 ? `.${j + 1}` : ''}`));
            (q.debug?.optionsDiagramBoundingBoxes ?? []).forEach(b => push(b, `Q${qNo}${b.label ? `(${b.label})` : ''}`));
        });
        return byPage;
    }, [questions]);

    /** Page images are 1-based by page number; label is "page-N". */
    const imageForPage = (page: number): string | undefined =>
        pageImages?.find(p => p.label === `page-${page}`)?.url ?? pageImages?.[page - 1]?.url;

    const pagesWithBoxes = [...boxesByPage.keys()].sort((a, b) => a - b).filter(p => !!imageForPage(p));
    const totalBoxes = [...boxesByPage.values()].reduce((n, l) => n + l.length, 0);

    if (questions.length === 0) return <p className="empty">No questions extracted</p>;

    return (
        <div className="extracted-questions-view">
            <div className="visual-segments-head">
                <div className="metadata-grid">
                    <div className="meta-item">
                        <label>Questions</label>
                        <p>{questions.length}</p>
                    </div>
                    <div className="meta-item">
                        <label>Diagram boxes</label>
                        <p>{totalBoxes}</p>
                    </div>
                </div>
                {pagesWithBoxes.length > 0 && (
                    <div className="bbox-page-triggers">
                        {pagesWithBoxes.map(page => (
                            <button
                                key={page}
                                type="button"
                                className="bbox-overlay-trigger"
                                onClick={() => setOpenPage(page)}
                            >
                                View boxes on page {page} ↗
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <ol className="extracted-questions-list">
                {questions.map((q, i) => (
                    <li key={i} className="extracted-question">
                        <div className="extracted-question-head">
                            <span className="tag">{q.questionType ?? '—'}</span>
                            <span className="extracted-question-no">Q{String(q.questionNumber ?? i + 1)}</span>
                            {q.sourcePageNumber != null && (
                                <span className="extracted-question-page">page {q.sourcePageNumber}</span>
                            )}
                            {q.topic && <span className="extracted-question-topic">{q.topic}</span>}
                        </div>
                        {/* Verbatim transcription of a maths/physics paper — the text
                            is full of LaTeX, and raw it reads as "$\frac{1}{2}mv^2$".
                            Rendered, it reads as the paper does. */}
                        <p className="extracted-question-text"><LatexText text={q.questionText ?? ''} /></p>
                        {(q.options?.length ?? 0) > 0 && (
                            <ul className="extracted-question-options">
                                {q.options!.map((o, j) => (
                                    <li key={j}><strong>{o.label})</strong> <LatexText text={o.text ?? ''} /></li>
                                ))}
                            </ul>
                        )}
                        {q.correctAnswer
                            ? <div className="extracted-question-answer">Answer: <LatexText text={String(q.correctAnswer)} /></div>
                            : <div className="extracted-question-answer empty">
                                No answer in the source — supplied by the enrichment pass
                            </div>}
                    </li>
                ))}
            </ol>

            {openPage != null && imageForPage(openPage) && (
                <BboxOverlayModal
                    imageUrl={imageForPage(openPage)!}
                    segments={boxesByPage.get(openPage) ?? []}
                    onClose={() => setOpenPage(null)}
                />
            )}
        </div>
    );
}
